import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector, batch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { addDays, differenceInDays, format, parse } from 'date-fns'
import {
  checkInputErrorMaxLength,
  checkInputErrorRequired,
  putCookie,
  useDatePicker,
  useEffectOnce,
  useInput,
  useQuery,
  useSelect
} from '@chilecompra/react-kit'
import { Input, Select, Typography } from '@chilecompra/react-kit/components'
import {
  FOREIGN_PROVIDER_DICTIONARY,
  QUOTE_SEARCH_FILTER_INITIAL_VALUES,
  QUOTE_SEARCH_FILTER_MAX_DIFF_DAYS,
  QUOTE_STATUSES,
  SEARCHS_KEYS_DICTIONARY
} from '../../config/settings/constants'
import routes from '../../config/settings/routes'
import { updateQuery } from '../../modules/utils/searches'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import QuoteSearchHeadingFiltersDisclaimer from './QuoteSearchHeadingFilters.disclaimer'
import { onCleanQuoteSuccess } from '../QuoteBasicInformation/QuoteBasicInformation.actions'
import { onClearSelectedOffers, onEnableOtherOffer } from '../SelectOfferDialog/SelectOfferDialog.actions'
import { onCleanErrorsPurchaseOrderStatus } from '../PurchaseOrderStatus/PurchaseOrderStatus.actions'
import {
  onApplyPriorizedBuyer,
  onClearMsgOrderPrioritized,
  onGetPrioritizedSupplierThunk,
  onGetSellerNotificationThunk,
  onGetSupplierHabilityV3Thunk,
  onMsgOrderPrioritized,
  onGetQuoteSearchThunk
} from './QuoteSearchHeadingFilters.actions.js'

import { onInitOnboardingTutorialThunk } from '../OnboardingDialog/OnboardingDialog.actions'

import {
  DatePicker,
  GridContainer,
  GridItem,
  HeadingFiltersForm,
  SearchButton,
  SearchIcon,
  Root
} from './QuoteSearchHeadingFilters.styles'

/*
 * The QuoteSearchHeadingFilters' container.
 */
const QuoteSearchHeadingFilters = () => {
  const { loading, supplierHability, supplierPrioritized } = useSelector(state => state.quoteSearchHeadingFilters)
  const {
    authInfo: { orgTaxID }
  } = useSelector(state => state.authorizationProvider)

  const navigate = useNavigate()
  const location = useLocation()
  const query = useQuery()
  const { isBuyer, isSeller } = useAuthorization()
  const [datesUpdates, setDatesUpdates] = useState(0)
  const dispatch = useDispatch()

  const {
    error: keywordError,
    value: keywordValue,
    onChange: keywordChange
  } = useInput({
    errorCallbacks: [checkInputErrorMaxLength(50)],
    initialValue: query.has('keyword') ? query.get('keyword') : QUOTE_SEARCH_FILTER_INITIAL_VALUES.keyword,
    overwriteCallback: () => (query.has('keyword') ? query.get('keyword') : QUOTE_SEARCH_FILTER_INITIAL_VALUES.keyword)
  })
  const {
    error: statusError,
    value: statusValue,
    onChange: handleStatusChange
  } = useSelect({
    initialValue: query.has('status') ? query.get('status') : QUOTE_SEARCH_FILTER_INITIAL_VALUES.status,
    overwriteCallback: () => (query.has('status') ? query.get('status') : QUOTE_SEARCH_FILTER_INITIAL_VALUES.status)
  })
  const {
    error: dateFromError,
    value: dateFromValue,
    onChange: handleFromDateChange,
    setValue: setDateFromValue
  } = useDatePicker({
    changeCallback: updatedValue => {
      setDatesUpdates(currentDatesUpdates => currentDatesUpdates + 1)

      if (Math.abs(differenceInDays(updatedValue, dateToValue)) <= QUOTE_SEARCH_FILTER_MAX_DIFF_DAYS) return

      setDateToValue(addDays(updatedValue, QUOTE_SEARCH_FILTER_MAX_DIFF_DAYS))
    },
    errorCallbacks: [
      checkInputErrorRequired(),
      updatedValue =>
        Math.abs(differenceInDays(updatedValue, dateToValue)) > QUOTE_SEARCH_FILTER_MAX_DIFF_DAYS
          ? `El rango no puede superar los ${QUOTE_SEARCH_FILTER_MAX_DIFF_DAYS} días`
          : ''
    ],
    errorDependencies: [datesUpdates],
    initialValue: query.has('dateFrom')
      ? parse(query.get('dateFrom'), 'dd/MM/yyyy', addDays(new Date(), -1))
      : parse(QUOTE_SEARCH_FILTER_INITIAL_VALUES.dateFrom, 'dd/MM/yyyy', addDays(new Date(), -1)),
    overwriteCallback: () =>
      query.has('dateFrom')
        ? parse(query.get('dateFrom'), 'dd/MM/yyyy', new Date())
        : parse(QUOTE_SEARCH_FILTER_INITIAL_VALUES.dateFrom, 'dd/MM/yyyy', addDays(new Date(), -1))
  })
  const {
    error: dateToError,
    value: dateToValue,
    onChange: handleToDateChange,
    setValue: setDateToValue
  } = useDatePicker({
    changeCallback: updatedValue => {
      setDatesUpdates(currentDatesUpdates => currentDatesUpdates + 1)

      if (Math.abs(differenceInDays(dateFromValue, updatedValue)) <= QUOTE_SEARCH_FILTER_MAX_DIFF_DAYS) return

      setDateFromValue(addDays(updatedValue, -QUOTE_SEARCH_FILTER_MAX_DIFF_DAYS))
    },
    errorCallbacks: [
      checkInputErrorRequired(),
      updatedValue =>
        Math.abs(differenceInDays(dateFromValue, updatedValue)) > QUOTE_SEARCH_FILTER_MAX_DIFF_DAYS
          ? `El rango no puede superar los ${QUOTE_SEARCH_FILTER_MAX_DIFF_DAYS} días`
          : ''
    ],
    errorDependencies: [datesUpdates],
    initialValue: query.has('dateTo')
      ? parse(query.get('dateTo'), 'dd/MM/yyyy', new Date())
      : parse(QUOTE_SEARCH_FILTER_INITIAL_VALUES.dateTo, 'dd/MM/yyyy', new Date()),
    overwriteCallback: () =>
      query.has('dateTo')
        ? parse(query.get('dateTo'), 'dd/MM/yyyy', new Date())
        : parse(QUOTE_SEARCH_FILTER_INITIAL_VALUES.dateTo, 'dd/MM/yyyy', new Date())
  })

  const handleSearch = updatedValues => event => {
    event.preventDefault()

    const dictionary = {
      ...QUOTE_SEARCH_FILTER_INITIAL_VALUES,
      ...Object.fromEntries(query.entries()),
      keyword: updatedValues.keywordValue,
      page: QUOTE_SEARCH_FILTER_INITIAL_VALUES.page,
      status: updatedValues.statusValue
    }

    if (dateFromError === '') dictionary.dateFrom = format(updatedValues.dateFromValue, 'dd/MM/yyyy')
    if (dateToError === '') dictionary.dateTo = format(updatedValues.dateToValue, 'dd/MM/yyyy')

    dictionary.searched = '1'

    updateQuery(query, dictionary)
    putCookie('search', query.toString())
    navigate({ pathname: routes.search, search: query.toString() }, { replace: true })
    dispatch(onGetQuoteSearchThunk({ query }))
  }

  const QUOTE_STATUSES_SELLER = QUOTE_STATUSES.filter(status => status.value !== 1) || []

  const resolverPrioritized = () => {
    if (isSeller) {
      if (!supplierPrioritized || !supplierHability) {
        dispatch(
          onGetPrioritizedSupplierThunk({
            orgTaxID,
            callback: isForeign => {
              dispatch(
                onGetSupplierHabilityV3Thunk({
                  orgTaxID,
                  isForeign: isForeign ? FOREIGN_PROVIDER_DICTIONARY.FOREIGN : FOREIGN_PROVIDER_DICTIONARY.NATIONAL
                })
              )
            }
          })
        )
      }
    } else {
      dispatch(onApplyPriorizedBuyer())
    }
  }

  const resolveBuyerCancelledNotification = () => {
    isBuyer && dispatch(onGetSellerNotificationThunk())
  }

  useEffectOnce(() => {
    batch(() => {
      dispatch(onClearMsgOrderPrioritized())
      dispatch(onEnableOtherOffer())
      dispatch(onClearSelectedOffers())
      dispatch(onCleanQuoteSuccess())
      dispatch(onCleanErrorsPurchaseOrderStatus())

      resolveBuyerCancelledNotification()
      resolverPrioritized()

      isSeller && dispatch(onInitOnboardingTutorialThunk())
    })
  })

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const priorized = params.get(SEARCHS_KEYS_DICTIONARY.PRIORIZED)
    handleParamChange(priorized)
  }, [location])

  const handleParamChange = priorized => {
    if (priorized) dispatch(onMsgOrderPrioritized())
    else dispatch(onClearMsgOrderPrioritized())
  }

  useEffect(() => {
    setDatesUpdates(currentDatesUpdates => currentDatesUpdates + 1)
  }, [loading])

  return (
    <Root>
      <QuoteSearchHeadingFiltersDisclaimer orgTaxID={orgTaxID} />

      <HeadingFiltersForm
        onSubmit={handleSearch({
          dateFromValue,
          dateToValue,
          statusValue,
          keywordValue
        })}
      >
        <GridContainer>
          <GridItem xs={12}>
            <Typography variant="h4" fontWeight="bold" padding="0 0 16px 0">
              {isSeller && 'Compra Ágil - Cotiza y vende de forma ágil y simple'}
              {isBuyer && 'Compra Ágil - Solicita cotizaciones de forma ágil y simple'}
            </Typography>
          </GridItem>
        </GridContainer>
        <GridContainer alignItems="center" height="100%" spacing={2}>
          <GridItem lg={4} md={4} xs={12}>
            <Input
              error={keywordError}
              label="ID o Palabra clave"
              loading={loading}
              value={keywordValue}
              onChange={keywordChange}
            />
          </GridItem>
          <GridItem lg={2} md={2} xs={12}>
            <Select
              error={statusError}
              label="Estado"
              loading={loading}
              options={isBuyer ? QUOTE_STATUSES : QUOTE_STATUSES_SELLER}
              placeholder="Todos"
              value={statusValue}
              onChange={handleStatusChange}
            />
          </GridItem>
          <GridItem lg={2} md={2} xs={12}>
            <DatePicker
              error={dateFromError}
              format="dd/MM/yyyy"
              label="Fecha desde"
              loading={loading}
              maxDate={dateToValue}
              tooltip={`El rango máximo de búsqueda tiene un límite de ${QUOTE_SEARCH_FILTER_MAX_DIFF_DAYS} días corridos.`}
              value={dateFromValue}
              onChange={handleFromDateChange}
            />
          </GridItem>
          <GridItem lg={2} md={2} xs={12}>
            <DatePicker
              error={dateToError}
              format="dd/MM/yyyy"
              label="Fecha hasta"
              loading={loading}
              minDate={dateFromValue}
              value={dateToValue}
              onChange={handleToDateChange}
            />
          </GridItem>
          <GridItem lg={2} md={2} xs={12}>
            <SearchButton color="primary" loading={loading} type="submit">
              <SearchIcon cursor color="100" /> Buscar
            </SearchButton>
          </GridItem>
        </GridContainer>
      </HeadingFiltersForm>
    </Root>
  )
}

export default QuoteSearchHeadingFilters
